import { Component, lazy, Suspense, useSyncExternalStore, type ReactNode } from 'react';

import { BannerAvatar } from '@/components/banner/BannerAvatar';
import { env } from '@/env';
import { storage } from '@/lib/storage';

/**
 * La figura del banner: il Qpet scelto (prototipo I-699, SOLO staging) oppure `BannerAvatar`. Sta fuori da `BannerAvatar`, che e' di ADR-0223 e resta intatto.
 * ⚠️ DUE letture del flag, come per Bridge: `env.featureQpetsEnabled` (i test lo accendono col mock di `@/env`) e il confronto LETTERALE dentro il `lazy()`
 * e qui, che il bundler piega: nella build di produzione il ramo Qpets e il suo codice non ci sono. Spento, o senza un Qpet scelto in `localStorage`
 * (assente, illeggibile): `BannerAvatar` com'e' oggi. Il Qpet vero lo valida `BannerQpet`; questo file non importa niente dalla zona Qpets se non dietro il `lazy()`.
 */
const BannerQpet = lazy(() =>
  import.meta.env.VITE_FEATURE_QPETS_ENABLED === 'true'
    ? import('@/components/qpets/BannerQpet').then((m) => ({ default: m.BannerQpet }))
    : Promise.reject(new Error('qpets: il banner non e compilato in questa build')),
);

// Gli stessi due valori di `lib/qpets/uova.ts` (la zona Qpets non si importa da qui): la chiave della scelta e l'evento che la annuncia.
const CHIAVE_SCELTO = 'qlash.qpets_scelto';
const EVENTO = 'qlash:qpets';
const iscriviti = (cb: () => void) => {
  window.addEventListener(EVENTO, cb);
  return () => window.removeEventListener(EVENTO, cb);
};

/**
 * Un `import()` rifiutato (rete, o l'hash di un chunk vecchio dopo un rilascio) o un render che lancia NON li ferma `Suspense`: senza questo salirebbero fino a
 * `RouteErrorBoundary` e farebbero cadere tutta la schermata, perche' il banner sta su ogni rotta. Qui cadono solo la figura, e ricade `BannerAvatar`.
 */
class Protezione extends Component<{ fallback: ReactNode; children: ReactNode }, { rotto: boolean }> {
  state = { rotto: false };
  static getDerivedStateFromError() {
    return { rotto: true };
  }
  render() {
    return this.state.rotto ? this.props.fallback : this.props.children;
  }
}

function FiguraConQpet({ striscia }: { striscia?: boolean }) {
  const id = useSyncExternalStore(iscriviti, () => storage.get(CHIAVE_SCELTO));
  const base = <BannerAvatar striscia={striscia} />;
  return id ? (
    <Protezione key={id} fallback={base}>
      <Suspense fallback={base}>
        <BannerQpet id={id} striscia={striscia} />
      </Suspense>
    </Protezione>
  ) : (
    base
  );
}

export function BannerFigura({ striscia }: { striscia?: boolean }) {
  return env.featureQpetsEnabled && import.meta.env.VITE_FEATURE_QPETS_ENABLED === 'true' ? <FiguraConQpet striscia={striscia} /> : <BannerAvatar striscia={striscia} />;
}
